import classNames from 'classnames';
import { kebabCase } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import MarketVolatilityNote from 'src/components/Buy/BuyFlow/ExpandedFundList/MarketVolatilityNote';
import { EditableDeductionListItem } from 'src/components/EditableDeductionListItem';
import { HelpButton } from 'src/components/Shared/HelpButton';
import { openModal } from 'src/redux/modules/modals/actions';
import {
  IRA_WITHHOLDING_WARNING_MODAL,
  STATE_TAX_DISCLOSURE_MODAL,
  STATE_WITHHOLDING_NON_MANDATORY,
} from 'src/redux/modules/modals/modals.module';
import {
  AGREEMENT_TYPES,
  specialStates,
  US_STATE_CODE,
} from 'src/settings/settings';
import { ModalName, Withholding } from 'src/types';
import {
  isAppSell,
  isAutomaticSell,
  isCollegeDistribute,
} from 'src/utils/transactions-util';
import {
  currency,
  isPershingVoluntaryState,
  isStateWithholdRegardless,
} from 'src/utils/utils';
import { Fund } from '../Exchange/types';
import { RedemptionAmountMessage } from '../Shared/Messages/RedemptionAmountMessage';
import { DeductionListItem } from './DeductionListItem';
import styles from './styles.module.scss';
import { isAPP, isRAS } from '../../utils/advice/advice-utility';
import AccountTeamModal from '../AccountTeamModal/AccountTeamModal';
import { ThunkDispatch } from 'redux-thunk';
require('animate.css/animate.min.css');

export interface DeductionListProps {
  customerWebViewEnabled: boolean;
  isSalesforceRep: boolean;
  federalWithholdingApplied?: Withholding['applied'];
  federalWithholdingDollars?: number;
  federalWithholdingPercent?: Withholding['percent'];
  fund: Fund;
  isRetirementLike?: boolean;
  grossAmountDollars: number;
  mandatoryWithholdingStates;
  marketVolatility?: Record<string, string>;
  RMData?: Record<string, string>;
  showCloseoutFee?: boolean;
  showMandatoryStateTaxes?: boolean;
  showNonMandatoryStateTaxes?: boolean;
  showTransactionFees?: boolean;
  showRedemptionAmountMessage?: boolean;
  stateIsEstimate: Withholding['isEstimate'];
  stateWithholdingApplied?: Withholding['isEstimate'];
  stateWithholdingDisabled?: Withholding['disabled'];
  stateWithholdingDollars?: number;
  stateWithholdingPercent?: Withholding['percent'];
  userState?: US_STATE_CODE;
  withholdingEditAction(toUpdate: Partial<Fund>): void;
  financialAdvisor?;
}

const DeductionList: React.FC<DeductionListProps> = ({
  customerWebViewEnabled,
  isSalesforceRep,
  children = 'Take-Home Amount',
  federalWithholdingApplied,
  federalWithholdingDollars,
  federalWithholdingPercent,
  isRetirementLike = false,
  showCloseoutFee = false,
  showTransactionFees = true,
  showRedemptionAmountMessage = false,
  stateIsEstimate,
  //stateWithholdingDollars,
  //stateWithholdingPercent,
  grossAmountDollars,
  fund: {
    stateWithholdingPercent,
    agreementID,
    stateWithholdingDollars,
    agreementType,
    closeoutFee,
    isTakeHomeEstimated,
    netDollarIsEstimate,
    sweep,
    ticker,
    transactionFees,
    address,
    productType,
  },
  mandatoryWithholdingStates,
  marketVolatility,
  RMData,
  withholdingEditAction,
  financialAdvisor,
}) => {
  const isAdvisoryProduct = isAPP(productType) || isRAS(productType);
  const showMandatoryStateTaxes = isAdvisoryProduct
    ? !isPershingVoluntaryState(address?.state)
    : mandatoryWithholdingStates?.includes(address?.state);
  const stateMandatoryMandatory = isStateWithholdRegardless(address.state);
  const stateWithholdingDisabled =
    address.state === US_STATE_CODE.AR
      ? false
      : !federalWithholdingApplied && !stateMandatoryMandatory;

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  // don't show if college
  if (isCollegeDistribute()) {
    return <React.Fragment />;
  }
  const showNonMandatoryStateTaxes = !showMandatoryStateTaxes;
  const modal: ModalName = showMandatoryStateTaxes
    ? STATE_TAX_DISCLOSURE_MODAL
    : STATE_WITHHOLDING_NON_MANDATORY;

  const statePercent = !stateWithholdingDisabled
    ? stateWithholdingPercent ?? 0
    : 0;
  const isFederalState = address.state && specialStates.includes(address.state);
  const netDollarAmount =
    (grossAmountDollars ?? 0) -
    (showTransactionFees ? transactionFees ?? 0 : 0) -
    (showCloseoutFee ? closeoutFee ?? 0 : 0) -
    (federalWithholdingApplied ? federalWithholdingDollars ?? 0 : 0) -
    (!stateWithholdingDisabled ? stateWithholdingDollars ?? 0 : 0);

  const isAnyEstimated =
    isTakeHomeEstimated || stateIsEstimate || netDollarIsEstimate;

  const isRothIRA =
    agreementType === AGREEMENT_TYPES.ROTH_IRA ||
    agreementType === AGREEMENT_TYPES.ROTH_ROLLOVER_IRA;

  return (
    <div className="deduction-list-container">
      {/* GROSS AMOUNT */}
      <DeductionListItem
        important
        className="gross-amount"
        label={classNames(
          { 'Est.': netDollarIsEstimate },
          'Redemption Amount in Dollars'
        )}
        value={`${currency(grossAmountDollars)}`}
      />

      {/* TRANSACTION FEE */}
      {/* {showTransactionFees && (
        <DeductionListItem
          className={styles['transaction-fee']}
          value={`- ${currency(transactionFees)}`}
        >
          <React.Fragment>
            Transaction Fee
            {!customerWebViewEnabled && isSalesforceRep ? (
              <span></span>
            ) : (
              <span>
                <ToolTip
                  message={`${
                    isAppSell() ? 'An account' : 'A fund'
                  } with a fee for buying or selling shares, similar to a brokerage fee or commission paid when a stock is bought or sold.`}
                />
              </span>
            )}
          </React.Fragment>
        </DeductionListItem>
      )} */}

      {/* CLOSEOUT FEE */}
      {showCloseoutFee && (
        <DeductionListItem
          className="closeout-fee"
          label="Administrative Fee"
          value={`- ${currency(closeoutFee)}`}
        />
      )}

      {/* EDITABLE FEDERAL TAXES */}
      {isRetirementLike && (
        <EditableDeductionListItem
          {...{
            federalWithholdingApplied,
            federalWithholdingDollars,
            federalWithholdingPercent,
            ticker,
          }}
          title={
            <React.Fragment>
              <span className={`strong ${styles['strong']}`}>
                Federal Income Tax Withholding{' '}
              </span>
            </React.Fragment>
          }
          onClickCheckbox={(checked, minTax) => {
            dispatch(
              withholdingEditAction({
                agreementID,
                federalWithholdingApplied: checked,
                federalWithholdingPercent: minTax,
              })
            );
          }}
          onClickHelp={() =>
            dispatch(
              openModal(IRA_WITHHOLDING_WARNING_MODAL, {
                agreementID,
                caller: 'tooltip',
                userState: address.state,
                mandatoryWithholdingStates: mandatoryWithholdingStates,
                isRoth: isRothIRA,
                showRothNotes: true,
              })
            )
          }
          onSave={(editingFederalValue) => {
            dispatch(
              withholdingEditAction({
                agreementID,
                federalWithholdingApplied: editingFederalValue !== 0,
                federalWithholdingPercent: editingFederalValue,
              })
            );
          }}
        >
          <div className={styles['deduction-list-amount']}>
            {federalWithholdingApplied
              ? `- ${currency(federalWithholdingDollars)}`
              : 'Declined'}
          </div>
        </EditableDeductionListItem>
      )}

      {/* STATE TAXES */}
      {/* NON-MANDATORY STATE */}
      {isRetirementLike && showNonMandatoryStateTaxes && (
        <DeductionListItem
          className="state-taxes"
          value={
            <AccountTeamModal
              RMData={RMData}
              financialAdvisor={financialAdvisor}
              productType={productType}
              ticker={ticker}
            />
          }
        >
          <React.Fragment>
            Estimated {address.state === 'DC' && <span>Mandatory</span>}
            {address.state} State Income Tax Withholding
            {address.state === 'MN' && (
              <span>
                <span className="deduction-list-federal-percent">
                  {' '}
                  ({statePercent}%)
                </span>
              </span>
            )}
            <HelpButton
              className={classNames(
                `e2e-state-withholding-non-mandatory-${kebabCase(ticker)}`,
                'deduction-list-federal-withholding-info'
              )}
              id="state-withholding-non-mandatory"
              onClick={() =>
                dispatch(
                  openModal(modal, {
                    caller: 'tooltip',
                    state: address.state,
                  })
                )
              }
            />
          </React.Fragment>
        </DeductionListItem>
      )}

      {/* MANDATORY STATE */}
      {isRetirementLike && showMandatoryStateTaxes && address.state && (
        <DeductionListItem
          className={classNames(['state-taxes', 'mandatory'])}
          value={
            stateWithholdingDisabled
              ? 'Declined'
              : `- ${currency(stateWithholdingDollars)}`
          }
        >
          <label>
            <span className="deduction-list-one-line ">
              Estimated {address.state} State Income Tax Withholding{' '}
              <span>
                <span className="deduction-list-federal-percent">
                  ({statePercent}%{isFederalState ? ' of Federal' : ''})
                </span>
              </span>
              <HelpButton
                ariaLabel="state-withholding-tooltip"
                className={classNames(
                  `e2e-state-withholding-info-link-${kebabCase(ticker)}`,
                  'deduction-list-state-withholding-info'
                )}
                id="retirement-state-withholding-info"
                onClick={() =>
                  dispatch(
                    openModal(modal, {
                      caller: 'tooltip',
                      state: address.state,
                    })
                  )
                }
              />
            </span>
          </label>
        </DeductionListItem>
      )}

      {/* Non-Retirement static fields */}
      {!isRetirementLike && isAutomaticSell() && (
        <React.Fragment>
          <DeductionListItem label="Federal Tax Not Withheld" value="N/A" />
          <DeductionListItem label="State Tax Not Withheld" value="N/A" />
        </React.Fragment>
      )}

      {/* TAKE-HOME AMOUNT (NET AMOUNT) */}
      <DeductionListItem
        important
        className={classNames([
          'deduction-list-net-container',
          styles['take-home-amount'],
        ])}
        value={currency(
          isRetirementLike ? netDollarAmount : grossAmountDollars
        )}
      >
        {classNames({
          // eslint-disable-next-line no-useless-computed-key
          ['Estimated ']: isAnyEstimated,
        })}
        {children}
      </DeductionListItem>

      {/* NOTES */}
      {isAnyEstimated && (customerWebViewEnabled || !isSalesforceRep) && (
        <div className={styles['estimate-note']}>
          Estimated amounts may differ due to market value at the time the trade
          is placed.
        </div>
      )}
      {sweep && (
        <div className={styles['sweep-note']}>
          <span>Important Note:</span> Before redeeming from a sweep account,
          consider any unsettled orders. Trades settle two business days after
          placement. Unsettled trades are not yet reflected in your current
          available balance.
        </div>
      )}
      {!isCollegeDistribute() &&
        !isAppSell() &&
        marketVolatility?.displayVolatilityMessage &&
        (customerWebViewEnabled || !isSalesforceRep) && (
          <MarketVolatilityNote />
        )}
      {showRedemptionAmountMessage && <RedemptionAmountMessage />}
    </div>
  );
};

export default DeductionList;
